/**
 * User Avatar Component
 * 
 * Displays user profile picture or fallback to initials avatar
 * - Shows profile picture if available
 * - Generates initials from firstName and lastName
 * - Consistent color based on user ID
 * - Supports multiple sizes
 */

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { User } from 'lucide-react'

// ============================================================================
// Component Props
// ============================================================================

export interface UserAvatarProps {
  user: {
    userNumber?: number
    firstName: string
    lastName: string
    profilePicture?: string
  }
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate initials from first and last name
 */
function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return `${first}${last}`
}

/**
 * Generate consistent background color based on user ID
 */
function getBackgroundColor(userNumber: number | undefined): string {
  const colors = [
    'bg-trilogy-blue',
    'bg-trilogy-red',
    'bg-trilogy-yellow',
    'bg-trilogy-navy',
    'bg-trilogy-gray',
  ]

  if (!userNumber) return colors[0]

  return colors[userNumber % colors.length]
}

/**
 * Get size classes for avatar
 */
function getSizeClasses(size: UserAvatarProps['size']): string {
  switch (size) {
    case 'sm':
      return 'h-8 w-8 text-xs'
    case 'md':
      return 'h-12 w-12 text-sm'
    case 'lg':
      return 'h-16 w-16 text-lg'
    case 'xl':
      return 'h-32 w-32 text-3xl'
    default:
      return 'h-12 w-12 text-sm'
  }
}

// ============================================================================
// Component Implementation
// ============================================================================

export function UserAvatar(props: UserAvatarProps) {
  const { user, size = 'md', className = '' } = props
  
  const initials = getInitials(user.firstName, user.lastName)
  const bgColor = getBackgroundColor(user.userNumber)
  const sizeClasses = getSizeClasses(size)

  return (
    <Avatar className={`${sizeClasses} ${className}`}>
      {user.profilePicture && (
        <AvatarImage 
          src={user.profilePicture} 
          alt={`${user.firstName} ${user.lastName}`} 
        />
      )}
      <AvatarFallback className={`${bgColor} text-white font-semibold`}>
        {initials || <User className="h-1/2 w-1/2" />}
      </AvatarFallback>
    </Avatar>
  )
}
